// The 1080 x 1920 PNG of a badge story, drawn on a canvas. The badge art has no
// text or fonts, so the composed SVG draws the same everywhere; the captions wait
// for the page font (JetBrains Mono) before they are drawn.
import { badgeInitials, composeBadgeSvg, loadBadgeLib, svgToDataUrl, tierStyle, type BadgeLib } from "../art";
import {
  STORY_CARD,
  STORY_H,
  STORY_LAYOUT as L,
  STORY_W,
  hexToRgba,
  patternTileSvg,
  storyText,
  storyTheme,
  type StoryInput,
  type StoryText,
  type StoryTheme,
} from "./storyTheme";

export const STORY_FONT = '"JetBrains Mono", ui-monospace, Menlo, monospace';

type Ctx = CanvasRenderingContext2D;

export interface StoryDeps {
  /** The parts library; loaded when omitted. null draws the fallback art. */
  lib?: BadgeLib | null;
  /** Where to draw (tests pass a canvas with a fake context). */
  createCanvas?: () => HTMLCanvasElement;
  loadImage?: (src: string) => Promise<CanvasImageSource>;
  /** Resolves once the caption font can be used. */
  fontsReady?: () => Promise<unknown>;
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("story image: an image failed to load"));
    img.src = src;
  });
}

async function pageFontsReady(): Promise<void> {
  const fonts = typeof document !== "undefined" ? document.fonts : undefined;
  if (!fonts) return;
  // A weight nobody on the page has used yet is not loaded until asked for.
  await Promise.all(["500", "600", "700"].map((w) => fonts.load(`${w} 48px "JetBrains Mono"`).catch(() => [])));
  await fonts.ready;
}

const font = (weight: number, px: number) => `${weight} ${px}px ${STORY_FONT}`;

/**
 * Greedy word wrap; long words are split. At most maxLines, the last one
 * ellipsised. `firstIndent` narrows the first line (the tier pill sits before it).
 */
export function wrapText(ctx: Ctx, text: string, maxWidth: number, maxLines: number, firstIndent = 0): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = "";
  const room = (index: number) => (index === 0 ? maxWidth - firstIndent : maxWidth);
  const fits = (s: string, index = lines.length) => ctx.measureText(s).width <= room(index);
  for (let word of words) {
    while (!fits(word) && word.length > 1) {
      // a single word wider than the line: cut it
      if (line) {
        lines.push(line);
        line = "";
        continue;
      }
      let cut = word.length - 1;
      while (cut > 1 && !fits(word.slice(0, cut))) cut--;
      lines.push(word.slice(0, cut));
      word = word.slice(cut);
    }
    const next = line ? `${line} ${word}` : word;
    if (fits(next)) line = next;
    else {
      if (line) lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  let last = kept[maxLines - 1];
  while (last.length > 1 && !fits(`${last}...`, maxLines - 1)) {
    // drop whole words first, then characters
    last = last.includes(" ") ? last.slice(0, last.lastIndexOf(" ")) : last.slice(0, -1);
  }
  kept[maxLines - 1] = `${last.trimEnd()}...`;
  return kept;
}

// ---------- layers ----------

function drawBackdrop(ctx: Ctx, theme: StoryTheme, pattern: CanvasImageSource | null) {
  const g = ctx.createLinearGradient(0, 0, 0, STORY_H);
  g.addColorStop(0, theme.top);
  g.addColorStop(1, theme.bottom);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, STORY_W, STORY_H);
  if (pattern) {
    const p = ctx.createPattern(pattern, "repeat");
    if (p) {
      ctx.fillStyle = p;
      ctx.fillRect(0, 0, STORY_W, STORY_H);
    }
  }
}

function drawLight(ctx: Ctx, theme: StoryTheme) {
  const { cx, cy } = L.badge;
  const R = L.lightR;
  // rays: a burst of wedges fading out from the badge
  const rg = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 1.5);
  rg.addColorStop(0, hexToRgba(theme.rays, theme.raysAlpha));
  rg.addColorStop(1, hexToRgba(theme.rays, 0));
  ctx.fillStyle = rg;
  ctx.beginPath();
  const period = (Math.PI * 2) / L.rays;
  for (let i = 0; i < L.rays; i++) {
    const a0 = -Math.PI / 2 - period / 4 + i * period;
    const a1 = a0 + period / 2;
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(a0) * R * 1.5, cy + Math.sin(a0) * R * 1.5);
    ctx.lineTo(cx + Math.cos(a1) * R * 1.5, cy + Math.sin(a1) * R * 1.5);
    ctx.closePath();
  }
  ctx.fill();
  // the soft light itself
  const lg = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
  lg.addColorStop(0, hexToRgba(theme.light, theme.lightAlpha));
  lg.addColorStop(0.45, hexToRgba(theme.light, theme.lightAlpha * 0.45));
  lg.addColorStop(1, hexToRgba(theme.light, 0));
  ctx.fillStyle = lg;
  ctx.fillRect(cx - R, cy - R, R * 2, R * 2);
}

function drawBadge(ctx: Ctx, img: CanvasImageSource | null, input: StoryInput) {
  const { cx, cy, size } = L.badge;
  if (img) {
    ctx.drawImage(img, cx - size / 2, cy - size / 2, size, size);
    return;
  }
  // fallback: the tier-coloured initials disc
  const ts = tierStyle(String(input.badge.tier));
  const r = size * 0.42;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = ts.fill;
  ctx.fill();
  ctx.lineWidth = 12;
  ctx.strokeStyle = "#000000";
  ctx.stroke();
  ctx.fillStyle = ts.ink;
  ctx.font = font(700, Math.round(r * 0.8));
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(badgeInitials(input.badge.name, input.badge.category), cx, cy);
}

function drawTopRow(ctx: Ctx, theme: StoryTheme, text: StoryText) {
  ctx.fillStyle = theme.ink;
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  ctx.font = font(700, 44);
  ctx.fillText(text.wordmark, L.pad, L.topY);
  ctx.globalAlpha = 0.78;
  ctx.textAlign = "right";
  ctx.font = font(500, 28);
  const kicker = wrapText(ctx, text.kicker, STORY_W - L.pad * 2 - 320, 1)[0] || "";
  ctx.fillText(kicker, STORY_W - L.pad, L.topY);
  ctx.globalAlpha = 1;
}

/** The tier pill: its width, and a function that draws it at (x, top). */
function tierPill(ctx: Ctx, text: StoryText, height: number) {
  const p = L.pill;
  ctx.font = font(700, 40);
  const w = Math.ceil(ctx.measureText(text.tierLabel).width) + p.padX * 2;
  const draw = (x: number, top: number) => {
    const ts = tierStyle(text.tier);
    ctx.fillStyle = "#000000";
    ctx.fillRect(x + p.shadow, top + p.shadow, w, height);
    ctx.fillStyle = ts.fill;
    ctx.fillRect(x, top, w, height);
    ctx.lineWidth = p.border;
    ctx.strokeStyle = "#000000";
    ctx.strokeRect(x + p.border / 2, top + p.border / 2, w - p.border, height - p.border);
    ctx.fillStyle = ts.ink;
    ctx.font = font(700, 40);
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(text.tierLabel, x + p.padX, top + height / 2 + 2);
  };
  return { w, draw };
}

/** Draws the caption card; returns its bottom edge. */
function drawCard(ctx: Ctx, text: StoryText): number {
  const c = L.card;
  const inner = c.w - 2 * (c.pad + c.border);
  const nameLH = Math.round(text.nameSize * 1.08);
  const lineLH = 52;

  ctx.font = font(700, text.nameSize);
  const nameLines = wrapText(ctx, text.name, inner, 2);
  // the tier pill leads the line; the holders fact wraps after it
  const pill = tierPill(ctx, text, lineLH - 6);
  ctx.font = font(600, 40);
  const factLines = text.fact ? wrapText(ctx, text.fact, inner - 16, 3, pill.w + L.pill.gap) : [];
  const lineCount = Math.max(1, factLines.length);

  const h = c.border + c.pad + nameLines.length * nameLH + 22 + lineCount * lineLH + 34 + 4 + 30 + 36 + 56 + c.pad + c.border;

  // hard shadow, fill, border
  ctx.fillStyle = "rgba(0, 0, 0, 0.88)";
  ctx.fillRect(c.x + c.shadow, c.y + c.shadow, c.w, h);
  ctx.fillStyle = STORY_CARD.fill;
  ctx.fillRect(c.x, c.y, c.w, h);
  ctx.lineWidth = c.border;
  ctx.strokeStyle = "#000000";
  ctx.strokeRect(c.x + c.border / 2, c.y + c.border / 2, c.w - c.border, h - c.border);

  const left = c.x + c.border + c.pad;
  const right = c.x + c.w - c.border - c.pad;
  let y = c.y + c.border + c.pad;

  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = STORY_CARD.ink;
  ctx.font = font(700, text.nameSize);
  for (const l of nameLines) {
    ctx.fillText(l, left, y + nameLH / 2);
    y += nameLH;
  }
  y += 22;

  pill.draw(left, y + 1);
  ctx.font = font(600, 40);
  ctx.fillStyle = STORY_CARD.ink;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  factLines.forEach((l, i) => {
    ctx.fillText(l, i === 0 ? left + pill.w + L.pill.gap : left, y + lineLH / 2);
    y += lineLH;
  });
  if (!factLines.length) y += lineLH;
  y += 34;

  ctx.fillStyle = STORY_CARD.ink;
  ctx.fillRect(left, y, right - left, 4);
  y += 4 + 30;

  ctx.font = font(500, 28);
  ctx.fillStyle = STORY_CARD.muted;
  ctx.fillText(text.by, left, y + 18);
  y += 36;

  ctx.font = font(700, 48);
  ctx.fillStyle = STORY_CARD.ink;
  const dateW = (() => {
    ctx.font = font(500, 32);
    return ctx.measureText(text.date).width;
  })();
  ctx.font = font(700, 48);
  const user = wrapText(ctx, text.username, right - left - dateW - 32, 1)[0] || "";
  ctx.fillText(user, left, y + 28);
  ctx.font = font(500, 32);
  ctx.fillStyle = STORY_CARD.muted;
  ctx.textAlign = "right";
  ctx.fillText(text.date, right, y + 30);
  ctx.textAlign = "left";

  return c.y + h;
}

/** "First on n450s": white, a black frame round a band in the tier's colour, tilted. */
function drawStamp(ctx: Ctx, label: string, tier: string) {
  const s = L.stamp;
  ctx.font = font(800, 48);
  const w = ctx.measureText(label).width + 92;
  ctx.save();
  ctx.translate(s.x + w / 2, s.y + s.h / 2);
  ctx.rotate((s.rotate * Math.PI) / 180);
  const x0 = -w / 2;
  const y0 = -s.h / 2;
  // hard shadow
  ctx.fillStyle = "rgba(0, 0, 0, 0.9)";
  ctx.fillRect(x0 + 10, y0 + 10, w, s.h);
  // the black frame, the tier band inside it, a thin black line, the white face
  ctx.fillStyle = "#000000";
  ctx.fillRect(x0, y0, w, s.h);
  ctx.fillStyle = tierStyle(tier).fill;
  ctx.fillRect(x0 + s.border, y0 + s.border, w - 2 * s.border, s.h - 2 * s.border);
  ctx.fillStyle = "#000000";
  const inBand = s.border + s.band;
  ctx.fillRect(x0 + inBand, y0 + inBand, w - 2 * inBand, s.h - 2 * inBand);
  ctx.fillStyle = "#ffffff";
  const face = inBand + 4;
  ctx.fillRect(x0 + face, y0 + face, w - 2 * face, s.h - 2 * face);
  ctx.fillStyle = "#000000";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = font(800, 48);
  ctx.fillText(label, 0, 3);
  ctx.restore();
}

// ---------- entry points ----------

/** Draw the story onto a fresh 1080 x 1920 canvas. */
export async function drawBadgeStory(input: StoryInput, deps: StoryDeps = {}): Promise<HTMLCanvasElement> {
  const lib = deps.lib !== undefined ? deps.lib : await loadBadgeLib().catch(() => null);
  const loadImage = deps.loadImage ?? loadImageElement;
  const canvas = deps.createCanvas ? deps.createCanvas() : document.createElement("canvas");
  canvas.width = STORY_W;
  canvas.height = STORY_H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("story image: this browser cannot draw on a canvas");

  const fonts = (deps.fontsReady ?? pageFontsReady)().catch(() => undefined);
  const theme = storyTheme(input.badge.tier);
  const text = storyText(input, lib);
  const { badge } = input;
  const badgeSvg = lib
    ? composeBadgeSvg(
        lib,
        { category: badge.category, tier: badge.tier, number: badge.number, count: input.count, code: badge.code, variant: "full" },
        { pixelSize: L.badge.size }
      )
    : null;
  const [badgeImg, patternImg] = await Promise.all([
    badgeSvg ? loadImage(svgToDataUrl(badgeSvg)).catch(() => null) : Promise.resolve(null),
    loadImage(svgToDataUrl(patternTileSvg(lib, badge.category, String(badge.tier), theme.pattern, theme.patternAlpha))).catch(
      () => null
    ),
  ]);
  await fonts;

  drawBackdrop(ctx, theme, patternImg);
  drawLight(ctx, theme);
  drawBadge(ctx, badgeImg, input);
  drawTopRow(ctx, theme, text);
  drawCard(ctx, text);
  if (text.stamp) drawStamp(ctx, text.stamp, String(input.badge.tier));
  return canvas;
}

export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("story image: PNG export failed"))), "image/png");
  });
}

/** The story as a PNG file, ready for a download or navigator.share. */
export async function drawBadgeStoryPng(input: StoryInput, deps: StoryDeps = {}): Promise<Blob> {
  const canvas = await drawBadgeStory(input, deps);
  return canvasToPngBlob(canvas);
}

export function storyFileName(code: string): string {
  return `n450s-badge-${code.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
}
